<template>
  <div class="balance" data-testid="balance-input">
    <header class="balance__header">
      <div class="balance__header-content">
        <BaseText size="p4">Disponível para alocar</BaseText>
        <BaseText size="p4" :color="NebraskaColors.elementPrimary">
          {{ marginAllocation.availableBalance.formatted }}
        </BaseText>
      </div>
    </header>
    <div class="balance__main">
      <HeadingDisplay
        title="Defina o saldo alocado"
        subtitle="Digite apenas valores inteiros, sem centavos."
      />
      <SingleInputMoney
        data-testid="single-input"
        :value="initialValue"
        :helper-text="helperText"
        :status="status"
        :shortcut-value="marginAllocation.amountButtons"
        has-shortcut
        class="balance__input"
        :has-fraction="false"
        @change="onChangeValue"
      />

      <DataTable
        data-testid="variation-table"
        class="balance__table"
        :has-interaction="false"
        show-divider
        :columns="tableColumns"
        :data="tableData"
      />
    </div>
    <ButtonContainer class="balance__button-container">
      <template #button-primary>
        <ButtonPrimary
          label="Continuar"
          size="large"
          :loading="dataForm.isUpdatingLimits"
          :disabled="isDisabled"
          @click="nextStep"
        />
      </template>
    </ButtonContainer>
  </div>
</template>

<script lang="ts" setup>
import { computed, inject, onMounted, Ref, ref } from 'vue';

import {
  BaseText,
  HeadingDisplay,
  DataTable,
  ButtonContainer,
  ButtonPrimary
} from '@warrenbrasil/nebraska-web';
import { SingleInputMoney } from '@/modules/common/components/SingleInputMoney';

import {
  IMarginAllocationPreviewV2,
  IMarginAllocationV2
} from '@/modules/trade/services/margin-allocation';

import {
  EMarginAllocationSteps,
  formattedData
} from '@/modules/trade/views/MarginAllocationV2/helpers';
import { moneyFormat } from '@/modules/common/helpers/currency';
import { NebraskaColors } from '@warrenbrasil/nebraska-tokens-web';

interface IDataForm {
  isUpdatingLimits: boolean;
  balance: { value: number; formatted: string };
}

const changeStep = inject('changeStep', (step: EMarginAllocationSteps) => ({
  step
}));
const updateDataForm = inject(
  'updateDataForm',
  (key: string, value: object) => ({
    key,
    value
  })
);

const dataForm = inject('dataForm') as Ref<IDataForm>;
const marginAllocationPreview = inject(
  'marginAllocationPreview'
) as Ref<IMarginAllocationPreviewV2>;
const marginAllocation = inject('marginAllocation') as Ref<IMarginAllocationV2>;

const balanceFormatted = ref('');
const balanceValue = ref(0);

const tableColumns = [
  {
    label: 'Ativo',
    align: 'left',
    getter: 'ticker'
  },
  {
    label: 'Exposição máxima',
    align: 'right',
    getter: 'maxExpose'
  }
];

const tableData = computed(() => {
  return formattedData(
    marginAllocationPreview.value.variationTable,
    balanceValue.value
  );
});

const isDisabled = computed(() => {
  return (
    balanceValue.value < minAllocation.value.value || status.value === 'error'
  );
});

const availableBalance = computed(() => {
  return marginAllocation.value.availableBalance.value;
});

const minAllocation = computed(() => {
  return marginAllocation.value.minimumMarginCapital;
});

const status = computed(() => {
  if (
    balanceValue.value > 0 &&
    balanceValue.value < minAllocation.value.value
  ) {
    return 'error';
  }

  if (balanceValue.value > 0 && balanceValue.value > availableBalance.value) {
    return 'error';
  }

  return '';
});

const helperText = computed(() => {
  if (
    balanceValue.value > 0 &&
    balanceValue.value < minAllocation.value.value
  ) {
    return `Valor mínimo para alocação é ${minAllocation.value.formatted}`;
  }

  if (balanceValue.value > 0 && balanceValue.value > availableBalance.value) {
    return `Valor superior ao saldo disponível ${marginAllocation.value.availableBalance.formatted}`;
  }

  return '';
});

const initialValue = computed(() => {
  return dataForm.value.balance.formatted.slice(3, -3);
});

function onChangeValue(data: { value: number }) {
  balanceFormatted.value = moneyFormat(data.value);
  balanceValue.value = data.value;
}

function nextStep() {
  updateDataForm('balance', {
    value: balanceValue.value,
    formatted: balanceFormatted.value
  });

  changeStep(EMarginAllocationSteps.DRAWDOWN);
}

onMounted(() => {
  balanceValue.value = dataForm.value.balance.value;
  balanceFormatted.value = moneyFormat(dataForm.value.balance.value);
});
</script>

<style lang="less" scoped>
@header-height: 130px;

.balance {
  &__header {
    background-color: @background-tertiary;

    &-content {
      display: flex;
      justify-content: space-between;
      max-width: 524px;
      margin: 0 auto;
      padding: @size-spacing-x450 @size-spacing-x400;
    }
  }

  &__main {
    max-width: 524px;
    margin: 0 auto;
    padding: @size-spacing-x1000 @size-spacing-x400 0;
  }

  &__input {
    margin-top: @size-spacing-x800;
  }

  &__table {
    margin-top: @size-spacing-x500;

    :deep(.cell) {
      padding: @size-spacing-x500 0;
    }

    :deep(.table-row:last-of-type) {
      td {
        border-bottom: none !important;
      }
    }
  }

  &__button-container {
    background-color: @background-secondary;
    width: 100%;
    position: fixed;
    bottom: 0;
    left: 0;
    z-index: 20;
  }
}
</style>
